import { useEffect, useState } from "react";
import { ArrowRight, Menu, MessageCircle, X } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import LanguageToggle from "./LanguageToggle";
import { useI18n } from "../contexts/I18nContext";
import { WHATSAPP_NUMBER } from "../lib/reviews";
import { normalize, pathFor } from "../lib/routing";
import logo from "../assets/logo.png";

const Logo = ({ home }) => (
  <Link to={home} className="flex items-center gap-3" data-testid="site-logo">
    <img src={logo} alt="Asado Xperience" className="h-11 w-11 object-contain shrink-0" />
    <span className="font-display text-lg tracking-[0.28em] uppercase text-[#F5F3F0]">
      Asado <span className="text-ember">Xperience</span>
    </span>
  </Link>
);

export default function Header() {
  const { t, lang } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", on, { passive: true });
    on();
    return () => window.removeEventListener("scroll", on);
  }, []);

  // Con el menú abierto no se scrollea el fondo, y Escape lo cierra.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") setMenuOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  // Al cambiar de ruta (incluido el cambio de idioma) el menú se cierra solo.
  useEffect(() => setMenuOpen(false), [location.pathname]);

  const linkCls =
    "text-[0.78rem] uppercase tracking-[0.22em] text-[#A3A3A3] hover:text-[#F5F3F0] transition-colors link-underline";

  // Las secciones viven en la home del idioma activo: desde otra ruta, primero navegamos.
  const home = pathFor("home", lang);
  const goToSection = (id) => (e) => {
    e.preventDefault();
    setMenuOpen(false);
    if (normalize(location.pathname) !== home) {
      navigate({ pathname: home, hash: `#${id}` });
      return;
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Las URLs salen del idioma activo (ver src/lib/routes.json): en español
  // apuntan a las rutas en español, y el selector ES/EN lleva a las inglesas.
  const SECTIONS = [
    ["experience", t.nav.experience],
    ["host", t.nav.host],
    ["reviews", t.nav.reviews],
    ["location", t.nav.location],
    ["faq", t.nav.faq],
    ["book", t.nav.book],
  ];
  const PAGES = [
    [home, t.nav.home],
    [pathFor("palermo", lang), t.footer.palermo_link],
    [pathFor("curso", lang), t.footer.curso_link],
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-charred/85 backdrop-blur-xl border-b border-white/5" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <Logo home={home} />
        <nav className="hidden lg:flex items-center gap-10">
          <a href={`${home}#experience`} onClick={goToSection("experience")} className={linkCls} data-testid="nav-experience">{t.nav.experience}</a>
          <a href={`${home}#host`} onClick={goToSection("host")} className={linkCls} data-testid="nav-host">{t.nav.host}</a>
          <a href={`${home}#reviews`} onClick={goToSection("reviews")} className={linkCls} data-testid="nav-reviews">{t.nav.reviews}</a>
          <a href={`${home}#location`} onClick={goToSection("location")} className={linkCls} data-testid="nav-location">{t.nav.location}</a>
        </nav>
        <div className="flex items-center gap-4">
          <LanguageToggle />
          <a href={`${home}#book`} onClick={goToSection("book")} className="btn-primary !hidden md:!inline-flex" data-testid="header-book-btn">
            {t.nav.book}
          </a>
          <button type="button" onClick={() => setMenuOpen((open) => !open)}
            aria-label={menuOpen ? t.nav.close_menu : t.nav.open_menu}
            aria-expanded={menuOpen} aria-controls="site-menu"
            data-testid="menu-toggle"
            className="h-11 w-11 -mr-2 flex items-center justify-center text-[#F5F3F0] hover:text-ember transition-colors">
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <div id="site-menu" hidden={!menuOpen} data-testid="site-menu"
        className="absolute top-full inset-x-0 max-h-[calc(100vh-5rem)] overflow-y-auto bg-charred/95 backdrop-blur-xl border-b border-white/10">
        <nav className="max-w-[1400px] mx-auto px-6 md:px-12 py-10 grid gap-10 sm:grid-cols-2">
          <div>
            <span className="overline flex items-center gap-3"><span className="divider-gold" />{t.nav.pages}</span>
            <ul className="mt-5 space-y-4">
              {PAGES.map(([to, label]) => (
                <li key={to}>
                  <Link to={to} onClick={() => setMenuOpen(false)}
                    className="font-display text-2xl md:text-3xl text-[#F5F3F0] hover:text-ember transition-colors">
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <span className="overline flex items-center gap-3"><span className="divider-gold" />{t.nav.sections}</span>
            <ul className="mt-5 space-y-3">
              {SECTIONS.map(([id, label]) => (
                <li key={id}>
                  <a href={`${home}#${id}`} onClick={goToSection(id)} className={linkCls}>{label}</a>
                </li>
              ))}
            </ul>
            <a href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noreferrer"
              onClick={() => setMenuOpen(false)}
              className="btn-ghost mt-8 inline-flex" data-testid="menu-whatsapp">
              <MessageCircle size={14} /> {t.nav.whatsapp} <ArrowRight size={14} />
            </a>
          </div>
        </nav>
      </div>

      {menuOpen && (
        <button type="button" tabIndex={-1} aria-hidden="true" onClick={() => setMenuOpen(false)}
          className="fixed inset-0 top-20 -z-10 bg-charred/60 cursor-default" />
      )}
    </header>
  );
}
